import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, FlaskConical, Scissors, Stamp, Globe } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import Marquee from "@/components/Marquee";

const CONFIDENCE = [
  { label: "HIGH", desc: "Supported by repeated observation across research periods and consistent with benchmark evidence. Suitable for policy influence.", color: "text-emerald-400 border-emerald-400/30" },
  { label: "MODERATE", desc: "Consistent within the research period but not yet confirmed across enough cycles. Informative, not decisive.", color: "text-amber-400 border-amber-400/30" },
  { label: "EXPLORATORY", desc: "Early signal or hypothesis. Published for the record and future verification — never as guidance.", color: "text-hussar-slate border-white/15" },
];

const PIPELINE = [
  { icon: FlaskConical, step: "Private Hussar Lab", text: "The private treasury system closes each week with an immutable Learning Report: observations, outcomes, anomalies." },
  { icon: Scissors, step: "Sanitizer", text: "The report is stripped of balances, accounts, providers, strategy parameters, and every operational detail. Only generalized findings survive." },
  { icon: Stamp, step: "Owner Approval", text: "Nothing publishes without explicit owner sign-off. The approval event is logged in the article's permanent history." },
  { icon: Globe, step: "Public Record", text: "The edition appears on hussartreasury.com with evidence cutoff, confidence label, and version — and is never silently rewritten." },
];

export default function Lab() {
  useEffect(() => {
    document.title = "Hussar Lab — Research Methodology | Hussar Treasury";
    let link = document.head.querySelector('link[rel="canonical"]');
    if (!link) { link = document.createElement("link"); link.setAttribute("rel", "canonical"); document.head.appendChild(link); }
    link.setAttribute("href", window.location.origin + "/lab");
  }, []);

  return (
    <div data-testid="lab-page" className="pt-40">
      <section className="px-6 sm:px-10 lg:px-16 max-w-[1600px] mx-auto pb-20">
        <Reveal>
          <div className="metadata-label mb-6">Hussar Lab</div>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-7xl text-hussar-ink tracking-tight leading-[1.05] max-w-4xl">
            The research engine behind Treasury.
          </h1>
          <p className="mt-8 max-w-2xl text-hussar-slate text-base sm:text-lg font-light leading-relaxed">
            Hussar Lab treats treasury operations as an empirical discipline. Every week of operation produces
            evidence; every piece of evidence is dated, labelled, and preserved. What can be shared without
            exposing the private system becomes Weekly Intelligence.
          </p>
        </Reveal>
      </section>

      <Marquee />

      {/* PIPELINE */}
      <section className="px-6 sm:px-10 lg:px-16 max-w-[1600px] mx-auto py-24 sm:py-32">
        <Reveal>
          <div className="metadata-label mb-4">The Publishing Pipeline</div>
          <h2 className="font-serif text-3xl sm:text-4xl text-hussar-ink tracking-tight mb-16">
            How private research becomes public record.
          </h2>
        </Reveal>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {PIPELINE.map((p, i) => (
            <Reveal key={p.step} delay={i * 0.1}>
              <div data-testid={`pipeline-step-${i}`} className="relative border border-white/5 bg-hussar-card p-8 h-full hover:border-hussar-gold/40 transition-colors duration-500">
                <div className="font-mono text-hussar-gold/40 text-xs tracking-[0.3em] mb-6">STEP {i + 1}</div>
                <p.icon className="text-hussar-gold mb-6" size={26} strokeWidth={1.25} />
                <h3 className="font-serif text-xl text-hussar-ink mb-4">{p.step}</h3>
                <p className="text-hussar-slate text-sm font-light leading-relaxed">{p.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* CONFIDENCE MATRIX */}
      <section className="px-6 sm:px-10 lg:px-16 max-w-[1600px] mx-auto pb-24 sm:pb-32">
        <Reveal>
          <div className="metadata-label mb-4">Confidence Taxonomy</div>
          <h2 className="font-serif text-3xl sm:text-4xl text-hussar-ink tracking-tight mb-6">
            Every conclusion carries its confidence on its sleeve.
          </h2>
          <p className="text-hussar-slate font-light max-w-2xl mb-14">
            Each edition is labelled before publication. Labels are assigned at the time of writing and are part
            of the permanent record — they are never upgraded retroactively after outcomes are known.
          </p>
        </Reveal>
        <div className="flex flex-col gap-4">
          {CONFIDENCE.map((c, i) => (
            <Reveal key={c.label} delay={i * 0.08}>
              <div data-testid={`confidence-${c.label.toLowerCase()}`} className={`border ${c.color} border-opacity-30 bg-hussar-card p-8 grid grid-cols-1 md:grid-cols-12 gap-4 items-start`}>
                <div className={`md:col-span-3 font-mono text-sm tracking-[0.3em] ${c.color.split(" ")[0]}`}>{c.label}</div>
                <p className="md:col-span-9 text-hussar-slate text-sm font-light leading-relaxed">{c.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* EVIDENCE CUTOFF */}
      <section className="px-6 sm:px-10 lg:px-16 max-w-[1600px] mx-auto pb-24 sm:pb-32">
        <Reveal>
          <div className="border border-hussar-gold/20 bg-hussar-card p-10 sm:p-14 grid grid-cols-1 lg:grid-cols-2 gap-10">
            <div>
              <div className="metadata-label mb-4">Evidence Cutoff Discipline</div>
              <h2 className="font-serif text-2xl sm:text-3xl text-hussar-ink leading-snug">
                Every edition declares the last day of its evidence.
              </h2>
            </div>
            <div className="text-hussar-slate font-light leading-relaxed text-sm sm:text-base space-y-4">
              <p>
                Research conclusions are only valid against the evidence available when they were drawn. Each edition
                carries an explicit evidence cutoff date and research period.
              </p>
              <p>
                If later evidence contradicts an earlier conclusion, the correction is published as a new version with
                a change note — the original remains in the archive. History is amended by addition, never by deletion.
              </p>
            </div>
          </div>
        </Reveal>
      </section>

      {/* CTA */}
      <section className="px-6 sm:px-10 lg:px-16 max-w-[1600px] mx-auto pb-28 sm:pb-40 text-center">
        <Reveal>
          <h2 className="font-serif text-3xl sm:text-4xl text-hussar-ink tracking-tight mb-10">Read the record as it forms.</h2>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/intelligence" data-testid="lab-to-archive-btn" className="btn-gold">
              Weekly Intelligence Archive <ArrowRight size={14} />
            </Link>
            <Link to="/governance" data-testid="lab-to-governance-btn" className="btn-ghost">
              Governance & Security
            </Link>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
